import React, { useMemo } from "react";
import { Box, Alert, Text, Stack } from "@mantine/core";
import { /*DOMPurify,*/ Config } from "dompurify";

export interface HTMLContentBlockProps {
  /** HTML content to display */
  htmlContent: string | null | undefined;

  /** Additional CSS class name for styling */
  className?: string;

  /** Whether to allow unsafe HTML (not recommended) */
  allowUnsafe?: boolean;

  /** Custom DOMPurify configuration */
  sanitizeConfig?: Config;

  themeConfig?: any;

  /** Callback fired when HTML content is rendered */
  onRender?: (() => void) | undefined;

  /** Callback fired when HTML sanitization fails */
  onError?: ((error: string) => void) | undefined;
}

export const HTMLContentBlock: React.FC<HTMLContentBlockProps> = ({
  htmlContent,
  className,
  allowUnsafe = false,
  sanitizeConfig,
  onRender,
  onError,
}) => {
  // Sanitize HTML content
  const sanitizedHtml = useMemo(() => {
    if (!htmlContent) {
      return "";
    }

    try {
      if (allowUnsafe) {
        // Use content as-is (not recommended for production)
        onRender?.();
        return htmlContent;
      }

      // Default sanitization config
      // const defaultConfig: Config = {
      //   ALLOWED_TAGS: [
      //     "p",
      //     "div",
      //     "span",
      //     "h1",
      //     "h2",
      //     "h3",
      //     "h4",
      //     "h5",
      //     "h6",
      //     "strong",
      //     "em",
      //     "u",
      //     "s",
      //     "br",
      //     "hr",
      //     "ul",
      //     "ol",
      //     "li",
      //     "a",
      //     "img",
      //     "table",
      //     "thead",
      //     "tbody",
      //     "tr",
      //     "th",
      //     "td",
      //     "blockquote",
      //     "pre",
      //     "code",
      //     "small",
      //     "sub",
      //     "sup",
      //     "style",
      //   ],
      //   ALLOWED_ATTR: [
      //     "href",
      //     "src",
      //     "alt",
      //     "title",
      //     "class",
      //     "id",
      //     "style",
      //     "target",
      //     "rel",
      //     "width",
      //     "height",
      //   ],
      //   ALLOW_DATA_ATTR: false,
      //   FORBID_TAGS: ["script", "object", "embed", "form", "input"],
      //   FORBID_ATTR: ["onclick", "onload", "onerror", "onmouseover"],
      //   ...sanitizeConfig,
      // };

      const sanitized = htmlContent; //DOMPurify.sanitize(htmlContent, defaultConfig);

      if (sanitized !== htmlContent) {
        console.warn("HTML content was sanitized for security");
      }

      onRender?.();
      return sanitized;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "HTML sanitization failed";
      console.error("HTMLContentBlock sanitization error:", error);
      onError?.(errorMessage);
      return null;
    }
  }, [htmlContent, allowUnsafe, sanitizeConfig, onRender, onError]);

  // Handle sanitization errors
  if (sanitizedHtml === null) {
    return (
      <Box
        className={`html-content-block html-content-block--error ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
          width: "100%",
        }}
      >
        <Stack align="center" gap="md" maw={400}>
          <Alert
            color="red"
            title="HTML Content Error"
            className="html-content-block__error-alert"
          >
            <Text
              size="sm"
              className="html-content-block__error-message"
              ta="center"
            >
              Unable to display HTML content due to sanitization failure.
            </Text>
          </Alert>
        </Stack>
      </Box>
    );
  }

  // Handle empty content
  if (!sanitizedHtml.trim()) {
    return (
      <Box
        className={`html-content-block html-content-block--empty ${
          className || ""
        }`}
        h="100%"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "var(--mantine-spacing-lg)",
          width: "100%",
        }}
      >
        <Text
          size="sm"
          c="dimmed"
          className="html-content-block__empty-message"
          ta="center"
        >
          No HTML content to display
        </Text>
      </Box>
    );
  }

  console.log("Render HTML");

  return (
    <Box
      className={`html-content-block ${className || ""}`}
      h="100%"
      style={{
        overflow: "auto",
        padding: "var(--mantine-spacing-md)",
        width: "100%",
      }}
    >
      <Box
        className="html-content-block__content"
        dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        style={{
          width: "100%",
          height: "100%",
          // Reset and style HTML content to match theme
          "& h1, & h2, & h3, & h4, & h5, & h6": {
            color: "var(--mantine-color-text)",
            fontFamily: "var(--mantine-font-family)",
            lineHeight: "var(--mantine-line-height)",
            marginBottom: "var(--mantine-spacing-sm)",
          },
          "& p": {
            color: "var(--mantine-color-text)",
            fontFamily: "var(--mantine-font-family)",
            fontSize: "var(--mantine-font-size-md)",
            lineHeight: "var(--mantine-line-height)",
            marginBottom: "var(--mantine-spacing-sm)",
          },
          "& a": {
            color: "var(--mantine-color-blue-6)",
            textDecoration: "none",
          },
          "& a:hover": {
            textDecoration: "underline",
          },
          "& img": {
            maxWidth: "100%",
            height: "auto",
          },
          "& table": {
            width: "100%",
            borderCollapse: "collapse",
            marginBottom: "var(--mantine-spacing-md)",
          },
          "& th, & td": {
            padding: "var(--mantine-spacing-xs)",
            borderBottom: "1px solid var(--mantine-color-gray-3)",
          },
          "& th": {
            fontWeight: 600,
            backgroundColor: "var(--mantine-color-gray-1)",
          },
        }}
      />
    </Box>
  );
};
